import { useCallback, useState } from 'react';

import { OPENING } from '../types';
import { viewerLostPermission } from './blockedViewer';
import { ApprovalRequest } from './types';

/**
 * Which rows a bulk decision can act on.
 *
 * An offer is excluded because deciding one means reading a document, which the
 * review panel is for. A row the viewer holds no open assignment on is excluded
 * because the server would refuse it — `Approvals::RecordDecision` enforces
 * identity, so a checkbox on it offers an action that 4xxes when taken. A row
 * whose viewer lost the approve permission is excluded for the same reason;
 * its own Approve and Reject say why.
 */
export const isSelectable = (row: ApprovalRequest): boolean =>
  row.subjectType === OPENING &&
  row.actionableStepIds.length > 0 &&
  !viewerLostPermission(row);

export interface QueueSelection {
  /** The selected rows, always a subset of what is on screen right now. */
  selected: ApprovalRequest[];
  /** Every row on this page a bulk decision could act on. */
  selectable: ApprovalRequest[];
  isSelected: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Selects every selectable row on the page, or clears them all. */
  toggleAll: () => void;
  clear: () => void;
}

/**
 * The queue's row selection (AXHE-3934).
 *
 * ## Derived against the rows on screen, not read straight out of the set
 *
 * The stored set can outlive the rows it names, and there are three ways it does:
 * a page turn, a filter change, and a single-row decision — which re-requests the
 * page without going anywhere. Reading the set directly means a bulk action can
 * post decisions for rows nobody can see, which is the worst version of this bug
 * because the confirmation names a count the reader never selected.
 *
 * So `selected` is the intersection of the set with the current page. The set may
 * hold stale ids; they can never be acted on. Callers clear it on a page or filter
 * change as well, which keeps the count in the bar honest rather than merely safe
 * — but the intersection is what makes it safe, and it holds even if a caller
 * forgets.
 */
export const useQueueSelection = (
  rows: ApprovalRequest[],
  /**
   * Rows already deciding on their own. Excluded from `selectable`, not merely
   * from `selected`: Select All builds its set from `selectable`, so anything
   * that only filtered the current selection would let the header checkbox put a
   * deciding row straight back in.
   */
  deciding: ReadonlySet<string> = new Set(),
): QueueSelection => {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());

  const selectable = rows.filter(
    row => isSelectable(row) && !deciding.has(row.id),
  );
  // Intersected with `selectable`, so a row that starts deciding leaves the
  // selection on its own — there is nothing to un-tick.
  const selected = selectable.filter(row => ids.has(row.id));

  const toggle = useCallback((id: string) => {
    setIds(current => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setIds(new Set()), []);

  // All-or-nothing against the page, not against the stored set: with a stale id
  // in the set, "some are selected" could otherwise be true while nothing on
  // screen is ticked.
  const allSelected =
    selectable.length > 0 && selected.length === selectable.length;
  const toggleAll = useCallback(() => {
    setIds(allSelected ? new Set() : new Set(selectable.map(row => row.id)));
  }, [allSelected, selectable]);

  return {
    selected,
    selectable,
    // Against `selectable`, not the raw set: a deciding row must read unticked
    // even while its id is still stored, or its own checkbox shows checked while
    // the bar has stopped counting it.
    isSelected: (id: string) =>
      ids.has(id) && selectable.some(row => row.id === id),
    toggle,
    toggleAll,
    clear,
  };
};
